import MobxReactForm from 'mobx-react-form';
import dvr from 'mobx-react-form/lib/validators/DVR';
import validatorjs from 'validatorjs';
import _get from 'lodash/get';
import AuthStore from '../../../../stores/AuthStore';
import ProfileStore from '../../../../stores/ProfileStore';
import NotificationStore from '../../../../stores/NotificationStore';
import GoogleTagManager from '../../../../services/GoogleTagManager';
import { GENDERS } from '../../../../framework/constants/profile';
import { PHONE_PREFIX_DEFAULT, PHONE_PREFIXES } from '../../../../framework/constants/forms';
import { i18nextInstance } from '../../../../../i18n';
export const fields = [
    {
        name: 'gender',
        label: 'modals:offerRequest.gender',
        placeholder: 'modals:offerRequest.genderPlaceholder',
        rules: 'required',
        extra: GENDERS,
        output: (data) => {
            return data ? data.value : null;
        }
    },
    {
        name: 'firstName',
        label: 'modals:offerRequest.firstName',
        placeholder: 'modals:offerRequest.firstNamePlaceholder',
        rules: 'required|string|firstName',
        value: ''
    },
    {
        name: 'lastName',
        label: 'modals:offerRequest.lastName',
        placeholder: 'modals:offerRequest.lastNamePlaceholder',
        rules: 'required|string|lastName',
        value: ''
    },
    {
        name: 'phoneMain',
        label: 'modals:offerRequest.phoneMain',
        placeholder: 'modals:offerRequest.phoneMainPlaceholder',
        fields: [
            {
                name: 'countryCode',
                placeholder: 'modals:offerRequest.countryCodePlaceholder',
                extra: PHONE_PREFIXES,
                value: PHONE_PREFIX_DEFAULT,
                rules: 'required',
                output: (data) => {
                    return data.value ? data.value : data;
                }
            },
            {
                name: 'areaCode',
                placeholder: 'modals:offerRequest.areaCodePlaceholder',
                type: 'tel',
                rules: 'required|phone_areaCode'
            },
            {
                name: 'phoneNumber',
                placeholder: 'modals:offerRequest.phoneNumberPlaceholder',
                type: 'tel',
                rules: 'required|phone_phoneNumber'
            }
        ],
        output: (value) => {
            const { countryCode, areaCode, phoneNumber } = value;
            return `${countryCode ? countryCode : ''}${areaCode}${phoneNumber}`;
        }
    },
    {
        name: 'timestamp',
        value: ''
    },
    {
        name: 'email',
        placeholder: 'modals:offerRequest.emailPlaceholder',
        type: 'email',
        label: 'modals:offerRequest.email',
        rules: 'required|email',
        value: ''
    },
    {
        name: 'agreement',
        label: 'modals:offerRequest.agreement',
        placeholder: 'modals:offerRequest.agreementPlaceholder',
        type: 'checkbox',
        rules: 'boolean|accepted',
        value: false
    }
];
const hooks = {
    onSuccess: (form) => {
        const { email, firstName, lastName, gender, phoneMain, agreement } = form.values();
        const createUserData = {
            email,
            userAddress: {
                firstName,
                lastName,
                gender,
                phoneMain
            },
            termsPermissions: {
                email: agreement,
                research: agreement,
                permissionText: i18nextInstance.t('modals:offerRequest.permissionText')
            }
        };
        if (AuthStore.isSale || AuthStore.isSaleRequest) {
            return ProfileStore.createUpdateUserProfileWithHelpSaleAgent(createUserData)
                .then(() => {
                GoogleTagManager.pushSuccessCustomerRegistration();
            });
        }
        return ProfileStore.createUserProfileWithoutPassword(createUserData)
            .then((tempPassword) => {
            GoogleTagManager.pushSuccessCustomerRegistration();
            return AuthStore.signIn(email, tempPassword, i18nextInstance.t.bind(i18nextInstance)); // ADV-287
        })
            .then(() => AuthStore.updateToken())
            .then(() => ProfileStore.getProfileData())
            .catch((reason) => {
            const { errors, id } = reason;
            if (id) {
                NotificationStore.addNotification({
                    message: i18nextInstance.t('modals:offerRequest.mailAlreadyInUse'),
                    level: 'error'
                });
                throw reason;
            }
            if (Array.isArray(errors)) {
                const details = [];
                errors.forEach((error) => {
                    if (error.detail) {
                        details.push(error.detail);
                    }
                    if (error.status === '500') {
                        NotificationStore.addNotification({
                            message: i18nextInstance.t('modals:offerRequest.saveDataFailed'),
                            level: 'error'
                        });
                        return;
                    }
                    const pointer = _get(error, 'source.pointer', '').split('/');
                    const fieldName = pointer.length ? pointer[pointer.length - 1] : '';
                    const errorDetail = _get(error, 'detail', '');
                    if (form.has(fieldName)) {
                        NotificationStore.addNotification({
                            message: `Error in field "${fieldName}": ${errorDetail}`,
                            level: 'error'
                        });
                        const field = form.$(fieldName);
                        if (field.hasNestedFields) {
                            field.each((field) => {
                                field.invalidate();
                            });
                        }
                        else {
                            field.invalidate(errorDetail);
                        }
                    }
                });
                GoogleTagManager.pushFailureCustomerRegistration(details.join(' | '));
            }
        });
    },
    onError: (form) => {
        const requiredFieldPaths = [];
        const validationFieldPaths = [];
        form.each((field) => {
            if (!field.hasNestedFields && field.hasError) {
                if (typeof field.rules === 'string' && field.rules.includes('required') && field.isEmpty) {
                    requiredFieldPaths.push(field.path);
                }
                else {
                    validationFieldPaths.push(field.path);
                }
            }
        });
        const errorMessages = [];
        if (requiredFieldPaths.length > 0) {
            errorMessages.push(`field is required: ${requiredFieldPaths.join(', ')}`);
        }
        if (validationFieldPaths.length > 0) {
            errorMessages.push(`validation errors: ${validationFieldPaths.join(', ')}`);
        }
        GoogleTagManager.pushFailureCustomerRegistration(errorMessages.join(' | '));
    }
};
const plugins = {
    dvr: dvr({
        package: validatorjs
    })
};
const options = {
    validateOnChange: true
};
export default new MobxReactForm({ fields }, { hooks, plugins, options });
